// @ts-check
import React from 'react';
import config from '~/config';
import { useGlobalLayout } from './Hocs/useGlobalLayout';

/**
 * @typedef HeadMetaItem
 * @property {string} name
 * @property {string} content
 *
 * @typedef {Record<string, unknown>} HeadSchemaItem
 * @property {string} context Will have '@' appended to front of 'context'.
 * @property {string} type Will have '@' appended to front of 'type'.
 * @property {string} [description]
 * @property {string} [name]
 * @property {string} [keywords] Space separated keywords.
 * @property {string[]} [sameAs]
 * @property {string} [image]
 * @property {string} [url]
 *
 * @typedef CsHeadOptions
 * @property {boolean} [appendSuffix=false] A value indicating whether the title should have the '| Bank OZK' suffix appended.
 * @property {HeadMetaItem[]} [meta]
 * @property {{ menuKey: string }} [attr]
 * @property {HeadSchemaItem[]} [schema]
 * @property {string} [titleTemplate]
 * @property {string} [title]
 */

/** @type {import('~/types/React').ReactFC<{ options: CsHeadOptions }>} */
export const StandardHeader = ({ children, options: { meta, schema, title, titleTemplate } }) => {
	const chosenTitle = titleTemplate ?? (title ? title + ' | Bank OZK' : 'Bank OZK');

	// Do ContentStack query
	const globalLayout = useGlobalLayout();

	return (
		<React.Fragment>
			{/* Favicon */}
			{!!globalLayout && <link rel="icon" type="image/x-icon" href={globalLayout?.favicon?.url} />}

			<link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
			<link href="https://use.typekit.net/art7dkc.css" rel="stylesheet" />
			{config.Robots === 'noindex' && <meta name="robots" content="noindex" />}
			<meta name="environment" content={`${config.Environment}`} />
			<title data-title={chosenTitle}>{chosenTitle}</title>

			{meta?.map((tag, idx) => (
				<meta name={tag.name} content={tag.content} key={idx} />
			))}

			{schema?.map((schema, idx) => {
				// Add '@' to the keys 'context' and 'type'.
				const { context, type, ...rest } = schema;
				Object.assign(rest, { '@context': context, '@type': type });

				return (
					<script type="json+ltd" key={idx}>
						{JSON.stringify(rest)}
					</script>
				);
			})}

			{/* Moved to below title to prevent (not set) in GA */}
			<script type="text/javascript">{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=false;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${config.TagManagerId}');`}</script>

			{children}
		</React.Fragment>
	);
};
